import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../common';
import { hasPermission } from '../../utils/permissions';

const NewLeadModal = ({ isOpen, onClose, onLeadCreated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '', // Keep for backward compatibility
    email: '',
    company: '',
    role: '', // Added role field
    secondary_email: '',
    phone: '',
    original_source: 'Manual Entry',
    latest_source: 'Manual Entry',
    status: 'new',
    notes: '', // Keep the notes field for backward compatibility
    country: '',
    assigned_to: '',
    call_attempts: 0,
    subscribed: true,
    address: ''
  });
  const [newNote, setNewNote] = useState(''); // This will be used for the new notes system
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const canAssignToOthers = hasPermission('leads', 'assignTo');

  const { first_name, last_name, name, email, company, role, secondary_email, phone, original_source, latest_source, status, notes, country, assigned_to, call_attempts, subscribed, address } = formData;
  
  // Fetch users with superuser or sales roles for assignment dropdown
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch all users
      const res = await axios.get('/api/auth/users', config);
      
      // Filter for only superuser, sales, and admin roles
      const filteredUsers = res.data.filter(user => 
        user.role === 'superuser' || user.role === 'sales' || user.role === 'admin'
      );
      
      setUsers(filteredUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      
      // Get current user
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        
        // If user doesn't have permission to assign to others,
        // automatically assign to themselves
        if (!canAssignToOthers) {
          setFormData(prev => ({
            ...prev,
            assigned_to: user.id
          }));
        }
      }
    }
  }, [isOpen, canAssignToOthers]);
  
  // Handle escape key to close modal
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Handle input changes
  const handleChange = e => {
    const { name, value } = e.target;
    
    // If user is trying to change assigned_to but doesn't have permission
    if (name === 'assigned_to' && !canAssignToOthers && value !== currentUser?.id) {
      setError('You do not have permission to assign leads to other users');
      return;
    }
    
    setFormData({ ...formData, [name]: value });
    
    // Clear error if it was a permission error
    if (error === 'You do not have permission to assign leads to other users') {
      setError('');
    }
  };

  // Handle new note changes
  const handleNewNoteChange = e => {
    setNewNote(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check for permission violations before submission
    if (!canAssignToOthers && formData.assigned_to && formData.assigned_to !== currentUser?.id) {
      setError('You do not have permission to assign leads to other users');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // If user doesn't have assign permission, ensure the lead is assigned to themselves or not assigned
      const dataToSubmit = {...formData};
      if (!canAssignToOthers && dataToSubmit.assigned_to && dataToSubmit.assigned_to !== currentUser?.id) {
        dataToSubmit.assigned_to = currentUser?.id || '';
      }

      // Create the lead (the initial creation still uses the legacy notes field)
      const res = await axios.post('/api/leads', dataToSubmit, config);
      
      // If we have a note in the new note field, add it using the new system
      if (newNote.trim()) {
        try {
          await axios.post(
            '/api/leads/${res.data.id}/notes',
            { content: newNote },
            config
          );
          
          // If we used both note systems, clear the legacy notes
          if (notes.trim()) {
            await axios.patch(
              '/api/leads/${res.data.id}/notes',
              { notes: '' },
              config
            );
          }
        } catch (noteErr) {
          console.error('Error adding note with new system:', noteErr);
          // Continue with the flow even if note creation fails
        }
      }
      
      onLeadCreated(res.data);
      setFormData({
        first_name: '',
        last_name: '',
        name: '',
        email: '',
        company: '',
        role: '', // Added role field
        secondary_email: '',
        phone: '',
        original_source: 'Manual Entry',
        latest_source: 'Manual Entry',
        status: 'new',
        notes: '',
        country: '',
        assigned_to: '',
        call_attempts: 0,
        subscribed: true,
        address: ''
      });
      setNewNote('');
      onClose();
    } catch (err) {
      // Check specifically for duplicate email error
      if (err.response && err.response.data.error_type === 'duplicate_email') {
        setError(err.response.data.message || 'A lead with this email already exists in your database.');
      } else {
        setError(
          err.response && err.response.data.message
            ? err.response.data.message
            : 'Failed to create lead. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      tabIndex="0"
    >
      <div className="bg-white rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="p-3 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Create New Lead</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-3">
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          
          {/* Two-column grid for form fields */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="first_name" className="block text-gray-700 text-sm font-bold mb-1">
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                id="first_name"
                value={first_name}
                onChange={(e) => {
                  // Update first_name and also update full name for backward compatibility
                  const newFirstName = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    first_name: newFirstName,
                    name: `${newFirstName} ${last_name}`.trim()
                  }));
                }}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="First name"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="last_name" className="block text-gray-700 text-sm font-bold mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                id="last_name"
                value={last_name}
                onChange={(e) => {
                  // Update last_name and also update full name for backward compatibility
                  const newLastName = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    last_name: newLastName,
                    name: `${first_name} ${newLastName}`.trim()
                  }));
                }}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Last name"
              />
            </div>
            
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={email}
                onChange={handleChange}
                required
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Email address"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="company" className="block text-gray-700 text-sm font-bold mb-1">
                Company
              </label>
              <input
                type="text"
                name="company"
                id="company"
                value={company}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Company name"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="role" className="block text-gray-700 text-sm font-bold mb-1">
                Role
              </label>
              <input
                type="text"
                name="role"
                id="role"
                value={role}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="E.g. CEO, Marketing Manager"
              />
            </div>
            
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="secondary_email" className="block text-gray-700 text-sm font-bold mb-1">
                Secondary Email
              </label>
              <input
                type="email"
                name="secondary_email"
                id="secondary_email"
                value={secondary_email}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Secondary email address"
              />
            </div>
            
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="phone" className="block text-gray-700 text-sm font-bold mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={phone}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Phone number"
              />
            </div>
            
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="country" className="block text-gray-700 text-sm font-bold mb-1">
                Country
              </label>
              <select
                name="country"
                id="country"
                value={country}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Select a country</option>
                <option value="Austria">Austria</option>
                <option value="Germany">Germany</option>
                <option value="Luxembourg">Luxembourg</option>
                <option value="Norway">Norway</option>
                <option value="Sweden">Sweden</option>
                <option value="Switzerland">Switzerland</option>
                <option value="United States">United States</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Address field */}
          <div className="col-span-2">
            <label htmlFor="address" className="block text-gray-700 text-sm font-bold mb-1">
              Address
            </label>
            <textarea
              name="address"
              id="address"
              value={address}
              onChange={handleChange}
              rows="2"
              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Street address, city, state, zip code"
            ></textarea>
          </div>
          
          {/* Second row of fields */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label htmlFor="original_source" className="block text-gray-700 text-sm font-bold mb-1">
                Original Source
              </label>
              <select
                name="original_source"
                id="original_source"
                value={original_source}
                onChange={(e) => {
                  // When original_source changes, update latest_source and legacy source to the same value for new leads
                  const newValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    original_source: newValue,
                    latest_source: newValue,
                    source: newValue
                  }));
                }}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="Manual Entry">Manual Entry</option>
                <option value="Website">Website</option>
                <option value="Phone Call">Phone Call</option>
                <option value="Email">Email</option>
                <option value="Referral">Referral</option>
                <option value="Social Media">Social Media</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="status" className="block text-gray-700 text-sm font-bold mb-1">
                Status
              </label>
              <select
                name="status"
                id="status"
                value={status}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="new">New</option>
                <option value="connected">Connected</option>
                <option value="HOT">HOT</option>
                <option value="disqualified">Disqualified</option>
                <option value="converted">Converted</option>
                <option value="unreachable">Unreachable</option>
              </select>
            </div>
          </div>
          
          {/* Third row of fields */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label htmlFor="assigned_to" className="block text-gray-700 text-sm font-bold mb-1">
                Assign To
              </label>
              <select
                name="assigned_to"
                id="assigned_to"
                value={assigned_to}
                onChange={handleChange}
                className={`shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${!canAssignToOthers ? 'bg-gray-100' : ''}`}
                disabled={!canAssignToOthers && currentUser?.id && assigned_to === currentUser.id}
              >
                {canAssignToOthers && <option value="">Not Assigned</option>}
                {users.map(user => (
                  <option 
                    key={user.id} 
                    value={user.id}
                    disabled={!canAssignToOthers && user.id !== currentUser?.id}
                  >
                    {user.name} ({user.role}) {user.id === currentUser?.id ? '(You)' : ''}
                  </option>
                ))}
              </select>
              {!canAssignToOthers && (
                <p className="mt-1 text-xs text-gray-500">
                  You can only assign leads to yourself.
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="call_attempts" className="block text-gray-700 text-sm font-bold mb-1">
                Call Attempts
              </label>
              <input
                type="number"
                name="call_attempts"
                id="call_attempts"
                min="0"
                max="10"
                value={call_attempts}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
              <p className="mt-0.5 text-xs text-gray-500">Initial count (0-10)</p>
            </div>
          </div>
          
          {/* Subscribed checkbox */}
          <div className="mb-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="subscribed"
                id="subscribed"
                checked={subscribed}
                onChange={(e) => setFormData({ ...formData, subscribed: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="subscribed" className="ml-2 block text-sm text-gray-700">
                Subscribed to marketing emails
              </label>
            </div>
          </div>
          
          {/* Notes field - using new notes system */}
          <div className="mb-3">
            <div className="flex justify-between">
              <label htmlFor="newNote" className="block text-gray-700 text-sm font-bold mb-1">
                Notes
              </label>
            </div>
            <textarea
              name="newNote"
              id="newNote"
              value={newNote}
              onChange={handleNewNoteChange}
              rows="3"
              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Add a note about this lead..."
            ></textarea>
            <p className="mt-1 text-xs text-gray-500">
              Notes will be saved with author information and timestamp.
            </p>
          </div>
          
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              className="!bg-gray-500 hover:!bg-gray-600 px-3 py-1.5 text-sm mr-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={loading}
              isLoading={loading}
            >
              {loading ? 'Creating...' : 'Create Lead'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewLeadModal;
